/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Checkbox, Stack } from '@fluentui/react';
import { CheckboxVisibility, IColumn, IDetailsColumnProps } from '@fluentui/react/lib/DetailsList';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { noop } from 'src/core/utils/base';
import { QnaMakerAnswerColumnRenderer } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerAnswerColumnRenderer';
import { QnaMakerContextColumnRenderer } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerContextColumnRenderer';
import { OptionalColumnVisibility } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerQnaPairsUiStore';
import { QnaMakerReviewSuggestionsQuestionColumnRenderer } from 'src/qnaMaker/appShell/qnaMakeReviewSuggestionsPage/QnaMakerReviewSuggestionsQuestionColumnRenderer';
import { QnaMakerMetadata } from 'src/qnaMaker/core/components/QnaMakerMetadata';
import { QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerQnaStore } from 'src/qnaMaker/core/stores/QnaMakerQnaStore';
import { QnaMakerSourceStore } from 'src/qnaMaker/core/stores/QnaMakerSourceStore';
import { QnaMakerContextTreeNode } from 'src/qnaMaker/core/types/QnaMakerContextTreeNode';

export type QnaMakerReviewSuggestionsTableProps = TableProps<QnaMakerQnaStore> & {
    optionalColumnsVisibility: OptionalColumnVisibility[];
    qnaPairToEditId: number;
    qnaPairsJson: QnaMakerQnaSwagger[];
    contextTreeMap: Map<number, QnaMakerContextTreeNode[]>;
    sourceStores: QnaMakerSourceStore[];
    isContextTreeCalculating: boolean;
    onSubmitEditQnaPair: (qnaId: number, newData: Partial<QnaMakerQnaSwagger>, isSuggestionsAccepted: boolean) => void;
    onChangeSuggestionSelection: (qnaId: number, suggestion: string, isSelected: boolean) => void;
    onChangeSelectAllSuggestions: (isSelected: boolean) => void;
    handleQnaContextualNavigation: (qnaId: number) => void;
    areQnaTableActionsDisabled: boolean;
    areAllSuggestionsSelected: boolean;
    qnaPairsSelectedSuggestion: { [key: string]: string[] };
};

const messages = defineMessages({
    context: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsTable.context',
        defaultMessage: 'Context'
    },
    questions: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsTable.questions',
        defaultMessage: 'Questions'
    },
    answerAndPrompts: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsTable.answerAndPrompts',
        defaultMessage: 'Answer and prompts'
    },
    suggestionsKind: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsTable.suggestionsKind',
        defaultMessage: 'Suggestion type'
    },
    smartSuggestions: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsTable.smartSuggestions',
        defaultMessage: 'Smart suggestions'
    },
    activeLearning: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsTable.activeLearning',
        defaultMessage: 'Active learning'
    },
    metadata: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsTable.metadata',
        defaultMessage: 'Metadata'
    },
    source: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsTable.source',
        defaultMessage: 'Source'
    },
    submit: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsTable.submit',
        defaultMessage: 'Submit'
    },
    cancel: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsTable.cancel',
        defaultMessage: 'Cancel'
    },
    selectAllSuggestions: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsTable.selectAllSuggestions',
        defaultMessage: 'Select all suggestions toggle'
    }
});

/**
 * A component displaying question and answer pairs along with their details in review suggestions page.
 */
export const QnaMakerReviewSuggestionsTable = observer((props: QnaMakerReviewSuggestionsTableProps) => {
    const {
        qnaPairsJson,
        contextTreeMap,
        onSubmitEditQnaPair,
        isContextTreeCalculating,
        optionalColumnsVisibility,
        areAllSuggestionsSelected,
        qnaPairsSelectedSuggestion,
        areQnaTableActionsDisabled,
        onChangeSuggestionSelection,
        onChangeSelectAllSuggestions,
        handleQnaContextualNavigation
    } = props;

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const [isPromptModalHidden, setIsPromptModalHidden] = React.useState(true);

    const { isSmartSuggestionEnabled } = rootStore.currentQnaMakerProjectStore;

    const onRenderContext = (item: QnaMakerQnaStore) => (
        <QnaMakerContextColumnRenderer
            qnaId={item.id}
            isContextTreeCalculating={isContextTreeCalculating}
            isContextOnly={item.dialog.isContextOnly}
            contextTrees={contextTreeMap.get(item.id) || []}
            onClickQna={handleQnaContextualNavigation}
        />
    );

    const onRenderQuestions = (item: QnaMakerQnaStore) => (
        <QnaMakerReviewSuggestionsQuestionColumnRenderer
            questions={item.questions}
            selectedSuggestions={qnaPairsSelectedSuggestion[item.id]}
            onEditQuestions={(questions, activeLearningSuggestions, isSuggestionsAccepted) =>
                onSubmitEditQnaPair(item.id, { questions, activeLearningSuggestions }, isSuggestionsAccepted)
            }
            onChangeAlternativeQuestionSelection={(suggestion: string, isSuggestionsSelected: boolean) =>
                onChangeSuggestionSelection(item.id, suggestion, isSuggestionsSelected)
            }
            areActionsDisabled={areQnaTableActionsDisabled}
            activeLearningSuggestions={item.activeLearningSuggestions}
        />
    );

    const onRenderAnswerAndPrompts = (item: QnaMakerQnaStore) => (
        <Stack styles={{ root: { alignSelf: 'flex-start' } }}>
            <QnaMakerAnswerColumnRenderer
                disabled
                onChange={noop}
                pairId={item.id}
                answer={item.answer}
                selectedQnaId={item.id}
                isRichTextEnabled={false}
                qnaPairsJson={qnaPairsJson}
                prompts={item.dialog.prompts}
                isContextOnly={item.dialog.isContextOnly}
                isPromptModalHidden={isPromptModalHidden}
                isAddActionDisabled={areQnaTableActionsDisabled}
                setIsPromptModalHidden={(value: boolean) => setIsPromptModalHidden(value)}
            />
        </Stack>
    );

    const onRenderMetadata = (item: QnaMakerQnaStore) => (
        <QnaMakerMetadata
            readOnly
            pairId={item.id}
            onChangeMetadata={noop}
            metadata={item.metadata}
            isAddActionDisabled={areQnaTableActionsDisabled}
        />
    );

    const onRenderQuestionsHeader = (_props: IDetailsColumnProps, defaultRenderer: (_: IDetailsColumnProps) => JSX.Element) => (
        <Stack horizontal verticalAlign="center">
            {isSmartSuggestionEnabled && (
                <Checkbox
                    ariaLabel={localize(messages.selectAllSuggestions)}
                    styles={{ checkbox: { borderRadius: '10px' }, root: { marginRight: '21px' } }}
                    checked={areAllSuggestionsSelected}
                    onChange={(_, isChecked) => onChangeSelectAllSuggestions(isChecked)}
                />
            )}
            {defaultRenderer(_props)}
        </Stack>
    );

    const onRenderSuggestionsKind = (item: QnaMakerQnaStore) => (
        <LuisText styles={{ root: { alignSelf: 'flex-start' } }}>
            {item.activeLearningSuggestions.every(a => a.totalAutoSuggestionCountFromGenerativeModel)
                ? localize(messages.smartSuggestions)
                : localize(messages.activeLearning)}
        </LuisText>
    );

    const columns: IColumn[] = [
        {
            key: 'questions',
            minWidth: 400,
            maxWidth: 500,
            isRowHeader: false,
            fieldName: 'questions',
            isResizable: true,
            isCollapsible: false,
            name: localize(messages.questions),
            onRender: onRenderQuestions,
            onRenderHeader: onRenderQuestionsHeader
        },
        {
            key: 'answerAndPrompts',
            minWidth: 250,
            maxWidth: 700,
            isRowHeader: false,
            fieldName: 'answerAndPrompts',
            isResizable: true,
            isCollapsible: false,
            name: localize(messages.answerAndPrompts),
            onRender: onRenderAnswerAndPrompts
        }
    ];

    if (isSmartSuggestionEnabled) {
        columns.push({
            key: 'suggestionsKind',
            minWidth: 250,
            maxWidth: 700,
            isRowHeader: false,
            fieldName: 'suggestionsKind',
            isResizable: true,
            isCollapsible: false,
            name: localize(messages.suggestionsKind),
            onRender: onRenderSuggestionsKind
        });
    }

    if (optionalColumnsVisibility.find(c => c.key === 'context').isVisible) {
        columns.unshift({
            key: 'context',
            minWidth: 250,
            maxWidth: 700,
            isRowHeader: true,
            fieldName: 'context',
            isResizable: true,
            isCollapsible: false,
            name: localize(messages.context),
            onRender: onRenderContext
        });
    }

    if (optionalColumnsVisibility.find(c => c.key === 'metadata').isVisible) {
        columns.push({
            key: 'metadata',
            minWidth: 200,
            maxWidth: 300,
            isRowHeader: false,
            fieldName: 'metadata',
            isResizable: true,
            isCollapsible: false,
            name: localize(messages.metadata),
            onRender: onRenderMetadata
        });
    }

    return <Table columns={columns} checkboxVisibility={CheckboxVisibility.hidden} {...props} />;
});
